import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useQuery } from '@apollo/client';
import ReactQuill from 'react-quill';
import { Button } from 'reactstrap';
import { GET_MESSAGE_TEMPLATES } from '../../Queries';
import { plainTextToHtml, htmlToPlainText } from '../htmlText';
import ShortcutHandler from './ShortcutHandler';
import CannedResponsePicker from './CannedResponsePicker';
import './MessageBodyEditor.css';
import 'react-quill/dist/quill.snow.css';

/**
 * Controlled message body input shared by the Comms composer and the mass message modal.
 * Renders a Quill editor when `richText` is set (email), a plain textarea otherwise, and
 * wires up the message template library (Templates button + `/` shortcut palette).
 */
export default function MessageBodyEditor({
    value = '',
    valueHtml = '',
    onChange,
    richText = false,
    placeholder,
    disabled = false,
    rows = 2,
    onSubmit,
    onCommandSelect,
    toolbarStart = null,
    toolbarEnd = null,
}) {
    const [cursorPosition, setCursorPosition] = useState(0);
    const [showShortcutHandler, setShowShortcutHandler] = useState(false);
    const [isTemplatePickerOpen, setTemplatePickerOpen] = useState(false);
    const textareaRef = useRef(null);

    const { data: templateData } = useQuery(GET_MESSAGE_TEMPLATES, {
        variables: { first: 100 },
        fetchPolicy: 'cache-first'
    });

    const templates = useMemo(() => {
        return templateData?.allMessageTemplates?.edges?.map(edge => edge.node) || [];
    }, [templateData]);

    const quillModules = useMemo(() => ({
        toolbar: [
            ['bold', 'italic', 'underline'],
            [{ list: 'ordered' }, { list: 'bullet' }],
            ['link'],
            ['clean'],
        ],
    }), []);

    // The body is cleared by the parent once a message is sent - drop any open palette with it
    useEffect(() => {
        if (!value) {
            setShowShortcutHandler(false);
            setTemplatePickerOpen(false);
        }
    }, [value]);

    const emitChange = (plainText, html = '') => {
        if (onChange) onChange(plainText, html);
    };

    const handleTextareaChange = (e) => {
        const newValue = e.target.value;
        const newCursorPos = e.target.selectionStart;
        emitChange(newValue);
        setCursorPosition(newCursorPos);
        const textBeforeCursor = newValue.substring(0, newCursorPos);

        // Check for / command
        const lastSlashIndex = textBeforeCursor.lastIndexOf('/');
        if (lastSlashIndex !== -1) {
            const textBeforeSlash = textBeforeCursor.substring(0, lastSlashIndex);
            const isAtStart = lastSlashIndex === 0;
            const isAfterWhitespace = /\s$/.test(textBeforeSlash);
            if (isAtStart || isAfterWhitespace) {
                const query = textBeforeCursor.substring(lastSlashIndex + 1);
                if (!/\s/.test(query)) {
                    setShowShortcutHandler(true);
                    return;
                }
            }
        }
        setShowShortcutHandler(false);
    };

    const handleKeyDown = (e) => {
        // Enter to send (unless Shift is held)
        if (onSubmit && e.keyCode === 13 && !e.shiftKey) {
            // Don't send if autocomplete is open
            if (showShortcutHandler) {
                return;
            }
            e.preventDefault();
            onSubmit(e);
        }

        // Escape to close modals/autocompletes
        if (e.key === 'Escape') {
            setShowShortcutHandler(false);
            setTemplatePickerOpen(false);
        }
    };

    const handleEmailEditorChange = (nextHtml) => {
        emitChange(htmlToPlainText(nextHtml || ''), nextHtml || '');
        if (showShortcutHandler) {
            setShowShortcutHandler(false);
        }
    };

    const handleTemplateSelect = (template) => {
        if (richText) {
            const htmlDraft = template?.contentHtml || plainTextToHtml(template?.content || '');
            const plainDraft = htmlToPlainText(htmlDraft) || template?.content || '';
            emitChange(plainDraft, htmlDraft);
            setTemplatePickerOpen(false);
            return;
        }

        const templatePlainContent = template?.content || htmlToPlainText(template?.contentHtml || '');
        if (!templatePlainContent) return;
        const currentPos = textareaRef.current?.selectionStart || value.length;
        const beforeCursor = value.substring(0, currentPos);
        const afterCursor = value.substring(currentPos);
        const separator = beforeCursor && !beforeCursor.endsWith('\n') ? '\n\n' : '';
        const newMessage = `${beforeCursor}${separator}${templatePlainContent}${afterCursor}`;
        emitChange(newMessage);
        setTemplatePickerOpen(false);

        setTimeout(() => {
            if (textareaRef.current) {
                const newPos = beforeCursor.length + separator.length + templatePlainContent.length;
                textareaRef.current.setSelectionRange(newPos, newPos);
                textareaRef.current.focus();
            }
        }, 0);
    };

    const handleCommandSelect = (command) => {
        if (command === 'template') {
            setTemplatePickerOpen(true);
            setShowShortcutHandler(false);
            return;
        }
        if (onCommandSelect) {
            onCommandSelect(command);
        }
        setShowShortcutHandler(false);
    };

    const editorHtmlValue = richText ? (valueHtml || plainTextToHtml(value)) : '';

    return (
        <div className="message-body-editor">
            <div style={{ position: 'relative' }}>
                {richText ? (
                    <div className="comms-send-rich-editor">
                        <ReactQuill
                            theme="snow"
                            value={editorHtmlValue}
                            onChange={handleEmailEditorChange}
                            modules={quillModules}
                            placeholder={placeholder}
                            readOnly={disabled}
                        />
                    </div>
                ) : (
                    <textarea
                        ref={textareaRef}
                        className="comms-send-textarea"
                        rows={rows}
                        name="body"
                        onKeyDown={handleKeyDown}
                        onChange={handleTextareaChange}
                        onClick={(e) => setCursorPosition(e.target.selectionStart)}
                        onSelect={(e) => setCursorPosition(e.target.selectionStart)}
                        onKeyUp={(e) => setCursorPosition(e.target.selectionStart)}
                        placeholder={placeholder}
                        value={value}
                        disabled={disabled}
                    />
                )}

                {!richText && showShortcutHandler && (
                    <ShortcutHandler
                        textareaRef={textareaRef}
                        message={value}
                        setMessage={(nextMessage) => emitChange(nextMessage)}
                        cursorPosition={cursorPosition}
                        templates={templates}
                        onTemplateSelect={handleTemplateSelect}
                        onCommandSelect={handleCommandSelect}
                        onClose={() => setShowShortcutHandler(false)}
                    />
                )}
            </div>

            <div className="comms-send-toolbar">
                {toolbarStart}

                <div style={{ position: 'relative', marginLeft: toolbarStart ? '8px' : 0 }}>
                    <Button
                        type="button"
                        color="light"
                        size="sm"
                        onClick={() => setTemplatePickerOpen(!isTemplatePickerOpen)}
                        style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}
                        disabled={disabled}
                    >
                        <i className="fa fa-bookmark" aria-hidden="true"></i>
                        <span>Templates</span>
                    </Button>

                    {isTemplatePickerOpen && (
                        <CannedResponsePicker
                            templates={templates}
                            onSelect={handleTemplateSelect}
                            onClose={() => setTemplatePickerOpen(false)}
                            isOpen={isTemplatePickerOpen}
                        />
                    )}
                </div>

                {toolbarEnd}
            </div>
        </div>
    );
}
